import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/PageLoader.css";

const HERO_VIDEO = "/backgroundVideo.mp4";
const MIN_MS = 1500;   // brief brand moment, then let users see content
const MAX_MS = 5000;   // safety cap so it never hangs on a slow/failed video

const PageLoader = ({ onComplete }) => {
  const [done, setDone] = useState(false);

  // Dismiss when BOTH (a) min time elapsed and (b) the hero video is ready,
  // with a hard cap so it never gets stuck.
  useEffect(() => {
    let minElapsed = false;
    let videoReady = false;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      setDone(true);
    };
    const maybeFinish = () => { if (minElapsed && videoReady) finish(); };

    const minTimer = setTimeout(() => { minElapsed = true; maybeFinish(); }, MIN_MS);
    const maxTimer = setTimeout(finish, MAX_MS);

    // Preload the hero video; "loadeddata" = first frame is ready to paint
    const v = document.createElement("video");
    v.src = HERO_VIDEO;
    v.muted = true;
    v.preload = "auto";
    const onReady = () => { videoReady = true; maybeFinish(); };
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("canplaythrough", onReady);
    v.addEventListener("error", onReady); // don't block if the video fails
    try { v.load(); } catch { onReady(); }

    return () => {
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("canplaythrough", onReady);
      v.removeEventListener("error", onReady);
      v.src = "";
    };
  }, []);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {!done && (
        <motion.div
          className="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
        >
          {/* logo: gentle fade + scale, then a slow breathing float */}
          <motion.img
            src="/8-insightsLogo.png"
            alt="8 Insights"
            className="loader__logo"
            initial={{ opacity: 0, scale: 0.9, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: [0, -5, 0] }}
            transition={{
              opacity: { duration: 1.1, ease: "easeOut" },
              scale:   { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
              y:       { duration: 3.6, ease: "easeInOut", repeat: Infinity },
            }}
          />

          {/* three softly pulsing dots */}
          <div className="loader__dots">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="loader__dot"
                animate={{ opacity: [0.25, 1, 0.25], scale: [0.85, 1, 0.85] }}
                transition={{ duration: 1.4, ease: "easeInOut", repeat: Infinity, delay: i * 0.22 }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageLoader;
